import { Component, OnInit, OnDestroy } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { EcitationSearchService } from 'src/services/ecitation-search.service';
import { EcitationUserService } from 'src/services/ecitation-user.service';

@Component({
    selector: 'app-public-citation-search',
    templateUrl: './public-citation-search.component.html',
    styleUrls: ['./public-citation-search.component.css'],
    standalone: false
})
export class PublicCitationSearchComponent implements OnInit, OnDestroy {
  listOfData: any;
  username: any;

  /** Controls which section is shown */
  showReportResults = false;
  activeTab = 1;
  showTableView = false;

  /** Strongly-typed form group for clarity (no behavior change) */
  searchForm!: FormGroup;

  /** Label map used when constructing the search summary string */
  labelMap: { [Key: string]: string } = {
    citationNumber: 'Citation number',
    issuedBy: 'Issued By',
    transmissionEndDate: 'Transmission End Date',
    transmissionStartDate: 'Transmission Start Date',
    status: 'Status',
  };

  outPut: any = [];
  agenciesList: any = [];
  selectedValue: any;
  showDetails = false;

  /** Existing sort state (kept) */
  sortName = '';
  sortDir = '';

  /** New a11y sort state used by aria-sort in the template */
  sortKey: string = 'citationNumber';
  sortAsc: boolean = false; // false means descending to match default 'desc'

  selectedItems: any = [];
  details: any = { Q: 'Queued', F: 'Failed', R: 'Rejected', D: 'Delivered' };

  /** Live status strings for screen readers (bound in template role="status" regions) */
  statusMessage = '';
  formErrorMessage = '';

  alert: any;
  private subscription!: Subscription;

  pageSize: number = 25;
  currentPage: number = 1;

  constructor(
    private ecitationSearchService: EcitationSearchService,
    private _formBuilder: FormBuilder,
    private ecitationUserService: EcitationUserService
  ) {
    this.subscription = this.ecitationUserService.reloadComponent1$.subscribe(() => {
      this.showReportResults = false;
      this.activeTab = 1;
      this.currentPage = 1;
      this.searchForm.reset(this.buidForm().value);
      this.statusMessage = 'Search form reset.';
    });
  }

  ngOnInit() {
    this.searchForm = this.buidForm();
    this.getAllAgencies();
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  buidForm() {
    return this._formBuilder.group({
      citationNumber: [''],
      issuedBy: ['ALL', [Validators.required]],
      transmissionEndDate: [''],
      transmissionStartDate: [''],
      status: [['ALL'], [Validators.required]],
    });
  }

  resetForm() {
    this.searchForm.reset(this.buidForm().value);
    this.currentPage = 1;
    this.formErrorMessage = '';
    this.statusMessage = 'Search form reset.';
  }

  getItems() {
    // Keeping original pagination behavior (server-driven); do not slice locally.
    return this.outPut.content;
  }

  onPageChange(pageNumber: number) {
    this.currentPage = pageNumber;
    this.onSearch(this.sortName, this.sortDir);
  }

  getTotalPages(): number {
    return this.outPut.totalPages;
  }

  getCurrentPageTotal() {
    if (this.currentPage * this.pageSize > this.outPut.totalElements) {
      return this.outPut.totalElements;
    } else {
      return this.currentPage * this.pageSize;
    }
  }

  getVisiblePages(): number[] {
    const totalVisiblePages = this.pageSize;
    const visiblePages: number[] = [];
    const startPage = Math.max(1, this.currentPage - Math.floor(totalVisiblePages / 2));
    const endPage = Math.min(this.getTotalPages(), startPage + totalVisiblePages - 1);
    for (let i = startPage; i <= endPage; i++) {
      visiblePages.push(i);
    }
    return visiblePages;
  }

  onSearch(name: string = 'citationNumber', dec: string = 'desc') {
    const params = this.searchForm.value;
    this.formErrorMessage = '';

    if (this.searchForm.valid) {
      const prePrams = {
        status: params.status,
        agencyName: params.issuedBy,
        citationNumber: params.citationNumber,
        transmissionStartDate: params.transmissionStartDate,
        transmissionEndDate: params.transmissionEndDate,
        frontendDestinations: 'ALL',
        page: this.currentPage - 1,
        size: this.pageSize,
        sort: `${name},${dec}`
      };

      const postParam = this.generateParams(prePrams);
      this.ecitationSearchService.getPublicSearchResults(postParam).subscribe((res: any) => {
        this.outPut = res;
        this.showTableView = params.citationNumber !== '';
        this.selectedItems = res.content;
        this.showReportResults = true;

        // Update live status for screen readers
        this.statusMessage = `${this.outPut?.totalElements ?? 0} result${(this.outPut?.totalElements ?? 0) === 1 ? '' : 's'} found. Displaying page ${this.currentPage} of ${this.outPut?.totalPages ?? 1}.`;
      });
    } else {
      // Keep original alert for UX parity, but also set an SR-friendly message
      alert('Please Fill all required Fields');
      this.formErrorMessage = 'Please fill all required fields.';
      this.statusMessage = this.formErrorMessage;
    }
  }

  onSort(event: any, name: any, isSortable: boolean) {
    if (isSortable) {
      let spanEle = event.target.querySelector('i') != null ? event.target.querySelector('i') : event.target,
        classList = spanEle.classList;
      let sortDir = 1;
      if (classList.contains('fa-sort') || classList.contains('fa-sort-up')) {
        classList.remove('fa-sort');
        classList.remove('fa-sort-up');
        classList.add('fa-sort-down');
        sortDir = -1;
      } else {
        classList.add('fa-sort-up');
        classList.remove('fa-sort-down');
      }
      this.sortBy(name, sortDir);
    }
  }

  sortBy(name: string, order: number) {
    const orderVal = order == 1 ? 'asc' : 'desc';
    this.sortName = name;
    this.sortDir = orderVal;

    // Update a11y sort state used by aria-sort
    this.sortKey = name;
    this.sortAsc = orderVal === 'asc';

    this.onSearch(name, orderVal);
  }

  generateParams(params: any) {
    const queryParams: string[] = [];
    for (let key in params) {
      if (params.hasOwnProperty(key)) {
        if (params[key] !== '') {
          if (Array.isArray(params[key])) {
            params[key].forEach((value: string | number | boolean) => {
              const encodeKey = encodeURIComponent(key);
              const encodedvalue = encodeURIComponent(value);
              queryParams.push(encodeKey + '=' + encodedvalue);
            });
          } else {
            const encodeKey = encodeURIComponent(key);
            const encodedvalue = encodeURIComponent(params[key]);
            queryParams.push(encodeKey + '=' + encodedvalue);
          }
        }
      }
    }
    return queryParams.join('&');
  }

  getAllAgencies() {
    this.ecitationSearchService.getAllAgencies().subscribe((data) => {
      this.agenciesList = data;
    });
  }

  showDetaislStatus(data: any) {
    const prePrams = {
      status: 'ALL',
      frontendDestinations: 'ALL',
      agencyName: 'ALL',
      citationNumber: data.citationNumber,
    };
    const postParam = this.generateParams(prePrams);
    this.ecitationSearchService.getPublicSearchResults(postParam).subscribe((res) => {
      this.selectedValue = res;
      this.showDetails = true;
      this.statusMessage = 'Detail status loaded.';
    });
  }

  deleteSelected() {
    const filteredResults = this.selectedItems.filter((x: any) => x.checked);
    this.ecitationSearchService.purgeRecords(filteredResults).subscribe(() => {
      this.alert = {
        isShown: true,
        message: 'Citation Deleted Sucessfully',
        type: 'success'
      };
      this.onSearch();
      this.statusMessage = 'Selected citations deleted successfully.';
    });
  }

  searchQuery() {
    return `You searched for: ${this.constructSearchString(this.searchForm)} `;
  }

  constructSearchString(formGroup: FormGroup): string {
    const searchParts: string[] = [];
    Object.keys(formGroup.controls).forEach(key => {
      const control: AbstractControl = formGroup.controls[key];
      const label = this.labelMap[key] || key;
      if (control.value !== null && control.value !== '') {
        searchParts.push(`${label}: ${control.value}`);
      }
    });
    return searchParts.join(', ');
  }
}
